// Rut planı (rut tanımlama) ekranları için ortak yardımcılar.
// Backend: /api/route-plans (RoutePlansController) — OneView SalesRoute* portu.

export const API_BASE = '/api/route-plans';

// 1=Pzt .. 7=Paz (RoutePlanInterval.DayNo ile aynı sıralama).
export const DAY_NAMES = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
export const DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 7];
export const MAX_WEEKS = 4;

export const STATUS_META = {
  Draft: { label: 'Taslak', tone: 'neutral' },
  Active: { label: 'Aktif', tone: 'ok' },
  Passive: { label: 'Pasif', tone: 'wait' },
};

export const STATUS_OPTIONS = [
  { value: 'Draft', label: 'Taslak' },
  { value: 'Active', label: 'Aktif' },
  { value: 'Passive', label: 'Pasif' },
];

export function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

// ApiResponse<T> { Data/data } sarmalayıcısını açar.
export function unwrap(payload) {
  return pick(payload, 'Data', 'data') ?? payload;
}

export function statusMeta(status) {
  return STATUS_META[status] || { label: status || '—', tone: 'neutral' };
}

// "yyyy-MM-dd" <input type=date> değerine çevirir.
export function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// <input type=date> değerini ISO'ya çevirir (gün başı, yerel).
export function fromDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

export function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR');
}

// "13.07.2026 14:32" — versiyon (ValidFrom/ValidTo) damgaları için.
export function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
}

// ── Ziyaret frekansları (backend FrequencyToWeeks ile birebir eşleşmeli) ──
export const FREQUENCY_OPTIONS = [
  { value: 'WEEKLY', label: 'Her Hafta', weeks: [1, 2, 3, 4] },
  { value: 'BIWEEKLY_ODD', label: 'Tek Hafta (1·3)', weeks: [1, 3] },
  { value: 'BIWEEKLY_EVEN', label: 'Zıt Hafta (2·4)', weeks: [2, 4] },
  { value: 'MONTHLY', label: 'Ayda Bir', weeks: [1] },
];

export function frequencyLabel(code) {
  if (!code) return '—';
  return FREQUENCY_OPTIONS.find((f) => f.value === code)?.label || code;
}

// ── Günler (1=Pzt..7=Paz) — tam ad + kısa ad + harita/legend rengi ──
export const DAY_OPTIONS = [
  { value: 1, label: 'Pazartesi', short: 'Pzt', color: '#0073ea' },
  { value: 2, label: 'Salı', short: 'Sal', color: '#7a3cff' },
  { value: 3, label: 'Çarşamba', short: 'Çar', color: '#00c875' },
  { value: 4, label: 'Perşembe', short: 'Per', color: '#fdab3d' },
  { value: 5, label: 'Cuma', short: 'Cum', color: '#e2445c' },
  { value: 6, label: 'Cumartesi', short: 'Cmt', color: '#0ea5e9' },
  { value: 7, label: 'Pazar', short: 'Paz', color: '#ec4899' },
];

export function dayLabel(no) {
  return DAY_OPTIONS.find((d) => d.value === Number(no))?.label || '—';
}

export function dayShort(no) {
  return DAY_OPTIONS.find((d) => d.value === Number(no))?.short || '—';
}

const DAY_FALLBACK_COLOR = '#94a3b8';
export function dayColor(no) {
  return DAY_OPTIONS.find((d) => d.value === Number(no))?.color || DAY_FALLBACK_COLOR;
}

// Geçerli (sayısal, sonlu) bir koordinat mı?
export function hasGeo(row) {
  const lat = Number(pick(row, 'latitude', 'Latitude'));
  const lng = Number(pick(row, 'longitude', 'Longitude', 'Longtitude'));
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
}

export function rowGeo(row) {
  return {
    lat: Number(pick(row, 'latitude', 'Latitude')),
    lng: Number(pick(row, 'longitude', 'Longitude', 'Longtitude')),
  };
}

// ── Rota optimizasyonu (mobildeki "Optimize" düğmesiyle aynı sezgisel) ──
// Haversine ile iki koordinat arası mesafe (km).
export function haversineKm(aLat, aLng, bLat, bLng) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Greedy nearest-neighbor: ilk konumlu noktadan başlar, her adımda en yakın
// ziyaret edilmemiş noktayı seçer (mobil TodayViewModel.optimizeRoute ile aynı
// sezgisel; mobilde başlangıç GPS, burada listenin ilk konumlu noktasıdır).
// Konumu olmayan satırlar göreli sıralarını koruyarak SONA eklenir.
export function optimizeByNearestNeighbor(rows) {
  const withGeo = rows.filter(hasGeo);
  const noGeo = rows.filter((r) => !hasGeo(r));
  if (withGeo.length < 2) return rows;
  const remaining = withGeo.slice();
  const ordered = [remaining.shift()];
  while (remaining.length) {
    const last = rowGeo(ordered[ordered.length - 1]);
    let bestIdx = 0;
    let bestD = Infinity;
    remaining.forEach((r, i) => {
      const g = rowGeo(r);
      const d = haversineKm(last.lat, last.lng, g.lat, g.lng);
      if (d < bestD) { bestD = d; bestIdx = i; }
    });
    ordered.push(remaining.splice(bestIdx, 1)[0]);
  }
  return [...ordered, ...noGeo];
}

// Sıralı satırların toplam rota uzunluğu (km) — optimizasyon kazancını göstermek için.
export function routeLengthKm(rows) {
  const geo = rows.filter(hasGeo).map(rowGeo);
  let sum = 0;
  for (let i = 1; i < geo.length; i += 1) {
    sum += haversineKm(geo[i - 1].lat, geo[i - 1].lng, geo[i].lat, geo[i].lng);
  }
  return sum;
}
